import axios, { type AxiosResponse } from 'axios';

export const useApiStore = defineStore('api', () => {
  const auth = useAuthStore();
  const { public: $env } = useRuntimeConfig();

  const accessToken = computed(
    () => auth.authResponseParams?.api_token?.access_token,
  );

  const api = axios.create({
    baseURL: $env.STREAMLABS_API_BASE_PATH,
    // withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    if (!config?.headers?.Authorization && accessToken.value) {
      config['headers']['Authorization'] = `Bearer ${accessToken.value}`;
    }

    return config;
  });

  let hasRetried = false;

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const shouldInterceptUrl = ![
        '/api/v5/oauth/token',
        '/api/v5/oauth/token/exchange',
      ].includes(originalRequest.url);

      const oauthToken = auth.authResponseParams?.oauth_token;
      const apiToken = auth.authResponseParams?.api_token;

      if (
        error.response?.status === 401 &&
        shouldInterceptUrl &&
        !hasRetried &&
        (apiToken || oauthToken)
      ) {
        /* We do exactly one retry per app launch, because of watchers re-triggering
         * requests when access token changes.
         */
        hasRetried = true;

        console.debug('Retrying request to', originalRequest.url);

        const retryWith = () => {
          if (oauthToken && !apiToken) {
            return legacyTokenExchange(oauthToken).then((r) => r.data.data);
          } else if (apiToken) {
            return refreshToken(apiToken.refresh_token).then((r) => r.data);
          } else {
            return Promise.reject();
          }
        };

        return retryWith()
          .then((response) => {
            auth.setAccessToken(response);

            /* The request interceptor does not seem to pickup on the
             * results of the call above, which is why we pass the new
             * token explicitly, we shouldn't rely on side-effects anyway.
             *
             * Technically we do not *really* need to retry due to the watchers,
             * but this is plumbing in the event we remove them.
             */
            return axios.request({
              ...originalRequest,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${response.access_token}`,
              },
            });
          })
          .catch((_e) => {
            /*
             * Logout the user if we have a token in storage but API requests are failing,
             * even after trying to refresh token or exchange a legacy one
             *
             * TODO: we should consider bringing window to front, but that might be disruptive
             * but also help users that don't even know they're logged out, if any left
             */
            console.debug(
              `Logging out user because request to ${originalRequest.url} was not authorized.`,
            );
            auth.authResponseParams = undefined;
          });
      }

      return Promise.reject(error);
    },
  );

  const w = <T>(url: string) => {
    return useDeferredAsyncData(
      url,
      async () => {
        /* Must reject with an Error: Nuxt pipes asyncData failures through
         * createError(), which reads `.statusText` off the rejection value and
         * throws on a bare `undefined`.
         */
        if (!accessToken.value) {
          return Promise.reject(new Error(`Not authenticated, skipping ${url}`));
        }

        return await api.get<T>(url).then(({ data }) => data);
      },
      {
        // if bearer token changes, refetch all the routes
        watch: [accessToken],
      },
    );
  };

  const refreshToken = (refreshToken: string) =>
    api.post('/api/v5/oauth/token', {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: $env.PLUGIN_OAUTH_CLIENT_ID,
    });

  const revokeToken = () => api.post('/api/v5/oauth/token/revoke');

  const legacyTokenExchange = (oauthToken: string) =>
    api.post(
      '/api/v5/oauth/token/exchange',
      { origin: 'obs-plugin' },
      {
        headers: { Authorization: `Bearer ${oauthToken}` },
      },
    );

  return {
    get: api.get,
    post: api.post,
    put: api.put,
    delete: api.delete,

    auth: {
      refreshToken,
      revokeToken,
      legacyTokenExchange,
    },

    streamLabels: {
      appSettings: w<StreamLabelsAppSettingsResponse>(
        `/api/v5/slobs/stream-labels/app-settings`,
      ),
      files: w<StreamLabelsFilesResponse>(
        `/api/v5/obs-plugin/stream-labels/files`,
      ),
      settings: w<StreamLabelsSettingsResponse>(
        `/api/v5/slobs/stream-labels/settings`,
      ),
    },
    multistream: {
      ingest: w<RstIngestResponse>(`/api/v5/obs-plugin/rst/ingest`),
      user: {
        platforms: w<RstUserPlatformsResponse>(
          `/api/v5/obs-plugin/rst/user/platforms`,
        ),
        profile: w<RstUserProfileResponse>(
          `/api/v5/obs-plugin/rst/user/profile`,
        ),
        targets: w<RstUserTargetsResponse>(
          `/api/v5/obs-plugin/rst/user/targets`,
        ),
        settings: w<RstUserSettingsResponse>(
          `/api/v5/obs-plugin/rst/user/settings`,
        ),
      },
    },
    golive: {
      twitch: {
        info: w<GoLiveTwitchInfoResponse>(
          '/api/v5/obs-plugin/go-live/twitch/info',
        ),
      },
      youtube: {
        info: w<GoLiveYouTubeInfoResponse>(
          '/api/v5/obs-plugin/go-live/youtube/info',
        ),
      },
      tiktok: {
        info: w<GoLiveTikTokInfoResponse>(
          '/api/v5/obs-plugin/go-live/tiktok/info',
        ),
        apply: w<{ url: string }>(`/api/v5/obs-plugin/go-live/tiktok/apply`),
        links: w<{ monitor: string }>(
          `/api/v5/obs-plugin/go-live/tiktok/links`,
        ),
      },
      facebook: {
        info: w<GoLiveFacebookInfoResponse>(
          '/api/v5/obs-plugin/go-live/facebook/info',
        ),
      },
      patreon: {
        info: w<GoLivePatreonInfoResponse>(
          '/api/v5/obs-plugin/go-live/patreon/info',
        ),
      },
    },
    platformInfo: w<UserPlatformInfoResponse>(
      `/api/v5/obs-plugin/user/platform-info`,
    ),
  };
});
